import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Plus, X } from '../../../../../components/icon/solar-icons.js'
import {
    createSettingsLocation,
    deleteSettingsLocation,
    fetchSettingsLocations,
    type SettingsLocation,
    updateSettingsLocation,
} from '../../../utils/portal-api.js'

export interface LocationsSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

interface LocationForm {
    name: string
    country_code: string
}

const emptyForm: LocationForm = { name: '', country_code: 'EE' }

export const LocationsSection = ({ lang, showToast }: LocationsSectionProps) => {
    const s = t.portal.settings.locations
    const [locations, setLocations] = useState<SettingsLocation[]>([])
    const [slideOpen, setSlideOpen] = useState(false)
    const [editing, setEditing] = useState<SettingsLocation | null>(null)
    const [form, setForm] = useState<LocationForm>(emptyForm)
    const [saving, setSaving] = useState(false)

    const reload = async () => {
        const r = await fetchSettingsLocations()
        if (r.ok) setLocations(r.data)
    }

    useEffect(() => {
        void reload()
    }, [])

    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setSlideOpen(true)
    }

    const openEdit = (loc: SettingsLocation) => {
        setEditing(loc)
        setForm({ name: loc.name, country_code: loc.country_code })
        setSlideOpen(true)
    }

    const close = () => {
        setSlideOpen(false)
        setEditing(null)
    }

    const save = async () => {
        if (!form.name || !/^[A-Za-z]{2}$/.test(form.country_code)) return
        setSaving(true)
        try {
            const payload = {
                name: form.name,
                country_code: form.country_code.toUpperCase(),
            }
            const result = editing
                ? await updateSettingsLocation(editing.id, payload)
                : await createSettingsLocation(payload)
            if (!result.ok) {
                showToast(result.error, 'error')
                return
            }
            showToast(editing ? s.updated(lang) : s.created(lang), 'success')
            close()
            await reload()
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (loc: SettingsLocation) => {
        const result = await deleteSettingsLocation(loc.id)
        if (!result.ok) {
            showToast(result.error || s.cannotDelete(lang), 'error')
            return
        }
        showToast(s.deleted(lang), 'success')
        await reload()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={Plus}
                    text={s.createLocation(lang)}
                    onClick={openCreate}
                />
            </div>

            {locations.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noLocations(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {locations.map((loc) => (
                        <div key={loc.id} className="portal-settings__data-row">
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">{loc.name}</div>
                                <div className="portal-settings__data-meta">
                                    {loc.country_code} · {s.employees(lang)}: {loc.employee_count}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                <button type="button" onClick={() => openEdit(loc)}>
                                    {s.edit(lang)}
                                </button>
                                <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => void handleDelete(loc)}
                                    disabled={loc.employee_count > 0}
                                    title={
                                        loc.employee_count > 0 ? s.cannotDelete(lang) : undefined
                                    }
                                >
                                    {s.delete(lang)}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={close} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editing ? s.editLocation(lang) : s.createLocation(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.name(lang)}
                                    value={form.name}
                                    onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.countryCode(lang)}
                                    value={form.country_code}
                                    inputProps={{ maxLength: 2 }}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            country_code: value.toUpperCase(),
                                        }))
                                    }
                                />
                                <small style={{ color: 'var(--portal-text-secondary)' }}>
                                    {s.countryCodeHint(lang)}
                                </small>
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={
                                    saving || !form.name || !/^[A-Za-z]{2}$/.test(form.country_code)
                                }
                                onClick={() => void save()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
